import { Dialog } from '@/components/atoms/Dialog';
import { ErrorMessage } from '@/components/atoms/ErrorMessage';
import { Textarea } from '@/components/atoms/Textarea';
import Button from '@/components/Button/newButtons';
import type { DashboardGetBusinessesSuccessProps } from '@/modules/dashboard/dashboardActions';
import type { BusinessQuery, Search } from '@/modules/dashboard/dashboardTypes';
import { AI } from '@/resources/assets/images';
import type { RootState } from '@/rootReducer';
import type {
	GetAiSearchProps,
	HttpGetAiSearchResponse
} from '@/services/aiSearchService';
import { sendAnalytics } from '@/services/analyticsService';
import { CSSProperties, useEffect, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { useBorderRotation } from '@/modules/sequencesRedesign/components/AiModal/hooks/useBorderRotation';
import { useMapboxKeyQuery } from '@/hooks/useMapBoxKeyQuery';
import { useAnimatedEllipses } from '@/hooks/useAnimatedEllipses';
import { useSearchTabContext } from '../SearchTabContext';
import SearchTabFiltersListItemHeader from '../SearchTabFiltersListItemHeader';
import { reverseGeocode } from '../src/helpers/reverseGeocode';
import { useBusinessDataMutation } from './src/hooks/useBusinessDataMutation';
import { useAiSearchFormMutation } from './src/hooks/useFormMutation';
import { useAiSearchFormValidation } from './src/hooks/useFormValidation';

type SearchTabAiSearchProps = {
	onFilterChange: (query: BusinessQuery) => void;
	filterCount: number;
};

export const SearchTabAiSearch = ({
	onFilterChange,
	filterCount
}: SearchTabAiSearchProps) => {
	const [query, user] = useSelector(
		(state: RootState) => [
			state.dashboard.get('query').toJS(),
			state.user.getIn(['authorize', 'user']).toJS()
		],
		shallowEqual
	);
	const { data: mapboxKeyData } = useMapboxKeyQuery();
	const mapboxAPIKey = mapboxKeyData?.data?.data?.mapBoxAPIKey;

	const [isClearFilterAlertModalOpen, setIsClearFilterAlertModalOpen] =
		useState(false);
	const [didAiReturnEmptyFilter, setDidAiReturnEmptyFilter] = useState(false);

	const {
		selectedListItem,
		setSelectedListItem,
		setSelectedLocation,
		setYelpReviewsFilterMax,
		setYelpReviewsFilterMin
	} = useSearchTabContext();
	const { setBusiness } = useBusinessDataMutation();
	const { register, handleSubmit, formState, getValues } =
		useAiSearchFormValidation();

	const handleSetBusinessData = (
		data: DashboardGetBusinessesSuccessProps['data']
	) => {
		setBusiness({
			data
		});
	};

	const handleSetFilterAppliedOnAiSearch = async (
		filters: HttpGetAiSearchResponse['filters']
	) => {
		if (!mapboxAPIKey) return;

		const searches: Search[] = [];

		filters.includes?.forEach((value, index) => {
			searches.push({ value, type: 0, index });
		});
		filters.excludes?.forEach((value, index) => {
			searches.push({ value, type: 1, index });
		});

		const coordinatesLocation = Array.isArray(filters.coordinates)
			? await reverseGeocode({
					coordinatesInput: filters.coordinates,
					accessToken: mapboxAPIKey
				})
			: null;

		const placeLocation =
			filters.state && filters.country
				? `${filters.state}, ${filters.country}`
				: filters.country;

		onFilterChange({
			...filters,
			coordinates: filters.coordinates
				? `[${filters.coordinates}]`
				: undefined,
			searches
		});

		setSelectedLocation(coordinatesLocation || placeLocation || '');
	};

	const handleCloseAiSearchList = () => {
		setSelectedListItem('');
	};

	const handleOpenAlertFilters = () => {
		setIsClearFilterAlertModalOpen(true);
	};

	const handleCancelNewFilters = () => {
		setIsClearFilterAlertModalOpen(false);
	};

	const handleApplyNewFilters = () => {
		setIsClearFilterAlertModalOpen(false);
		const searchQuery = getValues('filters');

		mutate({ filters: searchQuery });
	};

	const handleApplyYelpMinReviewsFilter = (min: number | undefined) => {
		if (!min) return null;
		setYelpReviewsFilterMin(min);
	};
	const handleApplyYelpMaxReviewsFilter = (max: number | undefined) => {
		if (!max) return null;
		setYelpReviewsFilterMax(max);
	};

	const { mutate, isLoading } = useAiSearchFormMutation({
		onSuccess: async ({ data }, variables) => {
			if (!data?.data?.filters) {
				setDidAiReturnEmptyFilter(true);

				return null;
			}

			handleSetBusinessData({
				count: data.data.count,
				cursor: data.data.cursor,
				result: data.data.businesses
			});

			await handleSetFilterAppliedOnAiSearch(data.data.filters);

			handleApplyYelpMinReviewsFilter(data.data.filters.minYelpReviews);
			handleApplyYelpMaxReviewsFilter(data.data.filters.maxYelpReviews);
			sendAnalytics({
				type: 'track',
				userId: user.email,
				eventName: 'Filters applied by AI search',
				properties: {
					query: variables.filters,
					appliedFilters: data.data.filters
				}
			});

			handleCloseAiSearchList();
		}
	});

	const boxRef = useBorderRotation(isLoading, 80);
	const ellipses = useAnimatedEllipses(isLoading);

	const clearSearchFilters = () => {
		const queryNew = { ...query };

		sendAnalytics({
			type: 'track',
			userId: user.email,
			eventName: 'User cleared all search filters',
			properties: {
				query: queryNew,
				querySearch: queryNew.searches
			}
		});

		delete queryNew.searches;
		delete queryNew.includes;
		delete queryNew.excludes;

		onFilterChange(queryNew);
	};

	const onSubmit = (value: GetAiSearchProps) => {
		if (isLoading) return null;

		setDidAiReturnEmptyFilter(false);

		if (filterCount > 0) {
			return handleOpenAlertFilters();
		}

		mutate(value);
	};

	useEffect(() => {
		let timeout: NodeJS.Timeout;

		if (didAiReturnEmptyFilter) {
			timeout = setTimeout(() => {
				setDidAiReturnEmptyFilter(false);
			}, 5 * 1000);
		}

		return () => {
			clearTimeout(timeout);
		};
	}, [didAiReturnEmptyFilter]);

	return (
		<>
			<div
				className={`search-tab-filters-list-item ${selectedListItem === 'AI Search' ? 'opened' : ''}`}
			>
				<SearchTabFiltersListItemHeader
					filterName="AI Search"
					filterCount={0}
					onClick={clearSearchFilters}
					displayClearFiltersButton={false}
					icon={
						<AI className="search-tab-filter-list-item-icon size-4" />
					}
				/>

				<div className="search-tab-filters-list-item-dropdown">
					<span className="block w-full border-b border-dm-gray first-of-type:-mb-2 first-of-type:-translate-y-4" />

					<header className="flex items-baseline gap-4 p-2">
						<p className="font-barlow text-sm text-dm-strong-mid-grey">
							Who are you trying to reach?
						</p>
					</header>

					{didAiReturnEmptyFilter && <EmptyFiltersMessage />}

					<section className="flex flex-col items-center justify-center gap-4">
						<form
							onSubmit={handleSubmit(onSubmit)}
							className="flex w-full flex-col gap-3"
						>
							<div
								ref={boxRef}
								data-is-loading={isLoading}
								className="flex overflow-hidden rounded-md border border-dm-gray data-[is-loading='true']:border-[0.15625rem] data-[is-loading='true']:border-[#0000] data-[is-loading='true']:[background:padding-box_var(--bg-color),border-box_var(--border-color)]"
								style={
									{
										'--angle': '0deg',
										'--border-color':
											'linear-gradient(var(--angle), #BC82F3, #F5B9EA, #8D99FF, #AA6EEE, #FF6778, #FFBA71, #C686FF)',
										'--bg-color':
											'linear-gradient(#212121, #212121)'
									} as CSSProperties
								}
							>
								<Textarea
									size="lg"
									className="rounded-none border-none p-2 text-sm"
									placeholder="I'm a marketing agency offering social media services, looking for small coffee shops in NYC that don’t have online presence"
									{...register('filters')}
								/>
							</div>

							<ErrorMessage
								message={formState.errors.filters?.message}
							/>

							{isLoading ? (
								<span className="flex h-8 w-full cursor-not-allowed select-none items-center rounded-lg border-2 border-dm-blue pl-[4.6875rem] text-sm font-semibold">
									Searching{ellipses}
								</span>
							) : (
								<Button
									type="submit"
									smart
									leftIcon
									fullWidth
									disabled={isLoading}
								>
									AI search
								</Button>
							)}
						</form>
					</section>
				</div>
			</div>

			<ClearFilterAlertModal
				isOpen={isClearFilterAlertModalOpen}
				onCloseModal={handleCancelNewFilters}
				onApplyNewFilters={handleApplyNewFilters}
			/>
		</>
	);
};

const EmptyFiltersMessage = () => {
	return (
		<div className="flex flex-col gap-2 p-2">
			<span className="w-fit rounded-full border-2 border-dm-orange2 px-2 py-1 font-barlow font-semibold text-dm-orange2">
				No matching filters
			</span>

			<p className="font-barlow text-sm text-dm-strong-mid-grey">
				Sorry, we couldn&apos;t find results for that. Try rephrasing
				the request.
			</p>
		</div>
	);
};

type ClearFilterAlertModalProps = {
	isOpen: boolean;
	onCloseModal: VoidFunction;
	onApplyNewFilters: VoidFunction;
};

const ClearFilterAlertModal = ({
	isOpen,
	onCloseModal,
	onApplyNewFilters
}: ClearFilterAlertModalProps) => {
	return (
		<Dialog.Root open={isOpen} onOpenChange={onCloseModal}>
			<Dialog.Content className="gap-8 border-none">
				<Dialog.Title className="flex items-center gap-2 text-xl">
					<AI className="[&>path]:fill-orange" />
					AI Search Will Reset Your Filters
				</Dialog.Title>

				<Dialog.Description>
					This will clear any filters you&apos;ve already set and
					start a new search from scratch.
				</Dialog.Description>

				<Dialog.Footer>
					<Dialog.Close asChild>
						<Button variant="text">Cancel</Button>
					</Dialog.Close>

					<Dialog.Close asChild>
						<Button variant="secondary" onClick={onApplyNewFilters}>
							Confirm
						</Button>
					</Dialog.Close>
				</Dialog.Footer>
			</Dialog.Content>
		</Dialog.Root>
	);
};
